import { supabase } from './supabase/client';
import type {
  ParsedStatementResult,
  ParseStatementError,
  ParseStatementErrorCode,
} from '@flows.cash/types';

// Coordinate extraction of a multi-page PDF plus cold-start module load can
// take a while on the first call.
const CLIENT_TIMEOUT_MS = 90_000;

export type ParseStatementResponse =
  | { data: ParsedStatementResult; error: null }
  | { data: null; error: ParseStatementError };

function errorResponse(code: ParseStatementErrorCode): ParseStatementResponse {
  return { data: null, error: { error: code } };
}

/**
 * Sends a bank/credit-card statement PDF (base64, no data-URI prefix) to the
 * parse-statement Edge Function. The PDF is parsed in memory server-side and
 * never stored. Returns deterministic transaction rows plus the
 * reconciliation result — Date/Amount/Type are parser-owned, no AI involved.
 * Never throws — all failures resolve to a ParseStatementError.
 */
export async function parseStatementPdf(
  pdfBase64: string
): Promise<ParseStatementResponse> {
  if (!pdfBase64) return errorResponse('statement_unavailable');

  const timeout = new Promise<ParseStatementResponse>((resolve) =>
    setTimeout(() => resolve(errorResponse('statement_unavailable')), CLIENT_TIMEOUT_MS)
  );

  const request = (async (): Promise<ParseStatementResponse> => {
    try {
      const { data, error } = await supabase.functions.invoke('parse-statement', {
        body: { pdfBase64 },
      });

      if (error) {
        // FunctionsHttpError carries the original Response as `context`.
        const status: number | undefined = (error as any)?.context?.status;
        if (status === 429) return errorResponse('rate_limited');
        if (status === 413) return errorResponse('file_too_large');
        if (status === 422) return errorResponse('unsupported_statement');
        if (status === 400) return errorResponse('invalid_pdf');
        return errorResponse('statement_unavailable');
      }

      if (!Array.isArray(data?.rows) || !data?.reconciliation) {
        return errorResponse('statement_unavailable');
      }

      return { data: data as ParsedStatementResult, error: null };
    } catch (err) {
      console.error('parseStatementPdf failed:', err);
      return errorResponse('statement_unavailable');
    }
  })();

  return Promise.race([request, timeout]);
}
